import { Camera as CameraIcon, FlipHorizontal, RotateCcw } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { Checkbox } from '@/design/components/checkbox';
import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';
import { cn } from '@/design/utils';
import type { MediaDevicesLike } from '@/features/persons/images/camera';
import { useCamera } from '@/features/persons/images/camera';
import type { CaptureCanvas } from '@/features/persons/images/capture';
import { captureFrame } from '@/features/persons/images/capture';
import type { DeviceStorage } from '@/features/persons/images/devices';

/**
 * Taking a photograph at the desk.
 *
 * **Two mirrors, and they are deliberately separate controls with different defaults.** The PREVIEW
 * is mirrored, because a person in front of a camera expects the screen to move the way a mirror
 * does and an unmirrored preview makes people turn the wrong way. What is STORED is un-mirrored by
 * default, because a mirrored identity photograph is back-to-front: a scar, a parting or the
 * lettering on a shirt is on the wrong side of the face somebody later compares against. One control
 * for both would mean an operator fixing the preview and silently changing the record.
 *
 * **The countdown exists so the photograph is of somebody ready to be photographed.** Three seconds,
 * announced, and it can be cancelled; the alternative is a button that fires instantly and a set of
 * records full of people looking at a mouse.
 *
 * **Every failure is a sentence and an action.** No camera, permission denied, the device
 * disappearing mid-session: each has its own message, and the denial names the browser setting,
 * because there is nothing this application can do about that one and the operator has to be told
 * where to go.
 */

/** How long the countdown runs, in whole seconds. */
export const COUNTDOWN_SECONDS = 3;

/** One tick of the countdown. */
const TICK_MS = 1000;

/** What the capture surface is showing. */
type Shot =
  | { readonly kind: 'none' }
  | { readonly kind: 'counting'; readonly remaining: number }
  /** A frame has been taken and is waiting to be kept or retaken. */
  | { readonly kind: 'taken'; readonly base64: string };

export function WebcamCapture({
  media,
  storage,
  createCanvas,
  busy,
  onCapture,
}: Readonly<{
  /** `null` when this browser exposes no camera API at all. */
  media: MediaDevicesLike | null;
  storage: DeviceStorage;
  /** Injected because `jsdom` gives no 2D context, so a capture is otherwise untestable. */
  createCanvas?: () => CaptureCanvas;
  busy: boolean;
  /** The frame, base64 with no data-URL prefix, exactly as the capture route demands. */
  onCapture: (base64: string) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const camera = useCamera(media, storage);
  const [mirrorPreview, setMirrorPreview] = useState(true);
  const [storeMirrored, setStoreMirrored] = useState(false);
  const [shot, setShot] = useState<Shot>({ kind: 'none' });
  /** True when the platform refused to start the preview, so it is showing a still. */
  const [stalled, setStalled] = useState(false);
  const video = useRef<HTMLVideoElement | null>(null);

  const live = camera.state.kind === 'live';

  useEffect(() => {
    const element = video.current;
    if (element === null || camera.state.kind !== 'live') {
      return;
    }
    element.srcObject = camera.state.stream;
    // A rejected `play()` is REPORTED rather than discarded. It happens when a browser's autoplay
    // policy refuses a stream this application was granted, and the symptom is a preview that shows
    // one frozen frame and looks live — so an operator lines somebody up and photographs a still.
    element.play().then(
      () => {
        setStalled(false);
      },
      () => {
        setStalled(true);
      },
    );
  }, [camera.state]);

  /**
   * One frame, read at the moment the shutter fires.
   *
   * A `useCallback` over the two things that decide what is produced — the canvas factory and the
   * stored-mirror choice — rather than a function the countdown closes over, so the effect below can
   * declare every dependency it actually has. The video element is a ref and is deliberately not a
   * dependency: it is the same element for the life of the live preview.
   */
  const take = useCallback((): string => {
    const element = video.current;
    if (element === null) {
      return '';
    }
    const canvas = createCanvas?.() ?? document.createElement('canvas');
    return captureFrame(element, {
      width: element.videoWidth,
      height: element.videoHeight,
      storeMirrored,
      canvas,
    });
  }, [createCanvas, storeMirrored]);

  // The countdown. A real timer rather than an animation frame, because it is a human-scale count
  // that has to keep time even when the tab is not painting.
  useEffect(() => {
    if (shot.kind !== 'counting') {
      return undefined;
    }
    // The last tick TAKES the shot rather than storing a zero the next render would act on: a
    // countdown that reached `remaining: 0` in state would have to fire the shutter from the effect
    // body, which is a synchronous state change inside an effect and one cascading render per
    // photograph. Counting 3, 2, 1 and then producing a frame is also what the operator sees.
    const timer = setTimeout(() => {
      setShot(
        shot.remaining <= 1
          ? { kind: 'taken', base64: take() }
          : { kind: 'counting', remaining: shot.remaining - 1 },
      );
    }, TICK_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [shot, take]);

  return (
    <div className="flex flex-col gap-3" data-testid="webcam">
      {camera.state.kind === 'unsupported' && (
        <Alert role="status">
          <AlertTitle>{t('person.images.noCameraTitle')}</AlertTitle>
          <AlertDescription>{t('person.images.noCameraBody')}</AlertDescription>
        </Alert>
      )}

      {camera.state.kind === 'no_camera' && (
        <Alert role="status" data-testid="camera-absent">
          <AlertTitle>{t('person.images.noCameraTitle')}</AlertTitle>
          <AlertDescription>{t('person.images.noCameraBody')}</AlertDescription>
        </Alert>
      )}

      {camera.state.kind === 'denied' && (
        <Alert variant="destructive" role="alert" data-testid="camera-denied">
          <AlertTitle>{t('person.images.deniedTitle')}</AlertTitle>
          <AlertDescription>{t('person.images.deniedBody')}</AlertDescription>
        </Alert>
      )}

      {camera.state.kind === 'lost' && (
        <Alert variant="destructive" role="alert" data-testid="camera-lost">
          <AlertTitle>{t('person.images.lostTitle')}</AlertTitle>
          <AlertDescription>{t('person.images.lostBody')}</AlertDescription>
        </Alert>
      )}

      {camera.state.kind === 'failed' && (
        <Alert variant="destructive" role="alert">
          <AlertTitle>{t('person.images.cameraFailedTitle')}</AlertTitle>
          <AlertDescription>{t('person.images.cameraFailedBody')}</AlertDescription>
        </Alert>
      )}

      {camera.state.kind === 'idle' && (
        <Button type="button" variant="outline" data-testid="start-camera" onClick={camera.open}>
          <CameraIcon aria-hidden />
          {t('person.images.startCamera')}
        </Button>
      )}

      {camera.state.kind === 'opening' && (
        <p className="text-sm text-muted-foreground">{t('person.images.opening')}</p>
      )}

      {live && (
        <>
          {camera.options.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="camera-device">{t('person.images.deviceLabel')}</Label>
              <NativeSelect
                id="camera-device"
                value={camera.selected ?? ''}
                onChange={(event) => {
                  camera.select(event.target.value);
                }}
              >
                {camera.options.map((option, index) => (
                  <option key={option.deviceId} value={option.deviceId}>
                    {option.label.length > 0
                      ? option.label
                      : t('person.images.deviceFallback', { index: index + 1 })}
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}

          {/*
            The video element stays MOUNTED while a shot is being looked at, and the still is drawn
            over it. Swapping one for the other unmounts the element the stream is attached to, and
            the effect that attaches it is keyed on the camera's state rather than on the shot — so
            the element that came back on "retake" had no `srcObject`, never played, and reported
            `videoWidth` 0. The second photograph of every session was blank, and the operator found
            out when the thumbnail came back wrong.
          */}
          <div className="relative overflow-hidden rounded-md border bg-muted">
            <video
              ref={video}
              muted
              playsInline
              aria-label={t('person.images.previewLabel')}
              className={cn('w-full', mirrorPreview && '-scale-x-100')}
              data-testid="camera-preview"
            />
            {shot.kind === 'taken' && (
              <img
                src={`data:image/jpeg;base64,${shot.base64}`}
                alt={t('person.images.shotAlt')}
                className="absolute inset-0 h-full w-full object-contain"
                data-testid="shot-preview"
              />
            )}
            {shot.kind === 'counting' && (
              <p
                aria-live="assertive"
                className="absolute inset-0 flex items-center justify-center text-6xl font-semibold text-foreground"
                data-testid="countdown"
              >
                {t('person.images.countdown', { seconds: shot.remaining })}
              </p>
            )}
          </div>

          {stalled && (
            <p role="alert" className="text-xs text-destructive" data-testid="preview-stalled">
              {t('person.images.previewStalled')}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={mirrorPreview}
                onChange={(event) => {
                  setMirrorPreview(event.target.checked);
                }}
              />
              <FlipHorizontal aria-hidden className="size-4" />
              <span>{t('person.images.mirrorPreview')}</span>
            </label>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={storeMirrored}
                onChange={(event) => {
                  setStoreMirrored(event.target.checked);
                }}
              />
              <span>{t('person.images.storeMirrored')}</span>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">{t('person.images.mirrorHint')}</p>

          <div className="flex flex-wrap gap-2">
            {shot.kind === 'none' && (
              <Button
                type="button"
                data-testid="take-photo"
                onClick={() => {
                  setShot({ kind: 'counting', remaining: COUNTDOWN_SECONDS });
                }}
              >
                <CameraIcon aria-hidden />
                {t('person.images.takePhoto')}
              </Button>
            )}
            {shot.kind === 'counting' && (
              <Button
                type="button"
                variant="outline"
                data-testid="cancel-countdown"
                onClick={() => {
                  setShot({ kind: 'none' });
                }}
              >
                {t('person.images.cancelCountdown')}
              </Button>
            )}
            {shot.kind === 'taken' && (
              <>
                <Button
                  type="button"
                  data-testid="keep-shot"
                  disabled={busy}
                  onClick={() => {
                    onCapture(shot.base64);
                    setShot({ kind: 'none' });
                  }}
                >
                  {t('person.images.keepShot')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="retake-shot"
                  onClick={() => {
                    setShot({ kind: 'none' });
                  }}
                >
                  <RotateCcw aria-hidden />
                  {t('person.images.retake')}
                </Button>
              </>
            )}
            <Button type="button" variant="ghost" data-testid="stop-camera" onClick={camera.close}>
              {t('person.images.stopCamera')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
